import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { TagIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import { tagApi } from '../../../shared/services/tagApi'
import { useTranslation } from 'react-i18next'

interface Tag {
  _id: string
  name: string
  color: string
  usageCount: number
}

interface TagCloudProps {
  selectedTags: string[]
  onTagClick: (tagId: string) => void
  onClearTags: () => void
  onDeleteTag?: (tagId: string) => void
}

// 태그 조회 커스텀 훅
const useTags = () => {
  return useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      return await tagApi.list()
    },
    // 태그는 사용자가 직접 바꾸는 데이터라 주기적 폴링 대신
    // 변경 시 쿼리 무효화로 갱신한다
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  })
}

const TagCloud: React.FC<TagCloudProps> = ({
  selectedTags,
  onTagClick,
  onClearTags,
  onDeleteTag,
}) => {
  const { t } = useTranslation()
  const { data: tags = [], isLoading } = useTags()
  const [searchQuery, setSearchQuery] = useState('')

  // 태그 삭제 핸들러
  const handleDeleteTag = (tagId: string, e: React.MouseEvent) => {
    e.stopPropagation() // 태그 클릭 이벤트 방지

    if (!onDeleteTag) {
      return
    }

    // 부모 컴포넌트의 삭제 핸들러 호출
    onDeleteTag(tagId)
  }

  // 태그 필터링
  const filteredTags = tags.filter((tag: Tag) =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // 태그 크기 계산 (사용 빈도에 따라, 해상도별 조정)
  const getTagSize = (usageCount: number) => {
    const maxUsage = Math.max(...tags.map((t: Tag) => t.usageCount), 1)

    // 해상도별 크기 조정
    let minSize = 12
    let maxSize = 18

    if (window.innerWidth >= 3840) {
      // 4K (7xl)
      minSize = 16
      maxSize = 24
    } else if (window.innerWidth >= 2560) {
      // QHD (5xl)
      minSize = 14
      maxSize = 22
    } else if (window.innerWidth >= 1600) {
      // 3xl
      minSize = 13
      maxSize = 20
    }

    const size = minSize + (usageCount / maxUsage) * (maxSize - minSize)
    return Math.round(size)
  }

  // 선택된 태그 정보 가져오기
  const selectedTagsInfo = tags.filter((tag: Tag) =>
    selectedTags.includes(tag._id)
  )

  if (isLoading) {
    return (
      <div className="p-4 3xl:p-5 7xl:p-6">
        <div className="animate-pulse">
          <div className="mb-2 h-4 w-16 rounded bg-gray-200 dark:bg-gray-700 3xl:mb-3 3xl:h-5 3xl:w-20 7xl:h-6 7xl:w-24"></div>
          <div className="space-y-2 3xl:space-y-2.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-6 w-20 rounded bg-gray-200 dark:bg-gray-700 3xl:h-7 3xl:w-24 7xl:h-8 7xl:w-28"
              ></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="border-t border-gray-200 p-4 dark:border-gray-700 3xl:p-5 7xl:p-6">
      <div className="mb-3 flex items-center justify-between 3xl:mb-4">
        <h3 className="flex items-center text-sm font-medium text-gray-900 dark:text-white 3xl:text-base 7xl:text-lg">
          <TagIcon className="mr-1 h-4 w-4 3xl:mr-1.5 3xl:h-5 3xl:w-5 7xl:h-6 7xl:w-6" />
          {t('tag.title')}
        </h3>
        {selectedTags.length > 0 && (
          <button
            onClick={onClearTags}
            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 3xl:text-sm 7xl:text-base"
          >
            {t('tag.reset')}
          </button>
        )}
      </div>

      {/* 선택된 태그들 표시 */}
      {selectedTags.length > 0 && (
        <div className="mb-3">
          <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
            {t('tag.selected')}
          </div>
          <div className="flex flex-wrap gap-1">
            <AnimatePresence>
              {selectedTagsInfo.map((tag: Tag) => (
                <motion.span
                  key={tag._id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="inline-flex cursor-pointer items-center rounded-md px-2 py-1 text-xs font-medium text-white hover:opacity-80"
                  style={{ backgroundColor: tag.color }}
                  onClick={() => onTagClick(tag._id)}
                >
                  {tag.name}
                  <XMarkIcon className="ml-1 h-3 w-3" />
                </motion.span>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* 태그 검색 */}
      {tags.length > 10 && (
        <div className="mb-3 3xl:mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t('tag.searchPlaceholder')}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500 3xl:px-4 3xl:py-2 3xl:text-sm 7xl:px-5 7xl:py-2.5 7xl:text-base"
          />
        </div>
      )}

      {/* 태그 클라우드 */}
      <div className="flex max-h-48 flex-wrap gap-1 overflow-y-auto 3xl:max-h-60 3xl:gap-1.5 7xl:max-h-72 7xl:gap-2">
        {filteredTags.length > 0 ? (
          filteredTags.map((tag: Tag) => {
            const isSelected = selectedTags.includes(tag._id)
            const fontSize = getTagSize(tag.usageCount)

            return (
              <motion.div
                key={tag._id}
                className="group relative"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.button
                  onClick={() => onTagClick(tag._id)}
                  className={`inline-flex items-center rounded-md px-2 py-1 font-medium transition-all duration-200 3xl:px-2.5 3xl:py-1 7xl:px-3 7xl:py-1.5 ${
                    isSelected
                      ? 'text-white ring-2 ring-offset-1 dark:ring-offset-gray-800'
                      : 'text-gray-700 hover:scale-105 dark:text-gray-300'
                  }`}
                  style={{
                    fontSize: `${fontSize * (window.innerWidth >= 1600 ? 1.2 : window.innerWidth >= 2560 ? 1.4 : window.innerWidth >= 3840 ? 1.6 : 1)}px`,
                    backgroundColor: isSelected ? tag.color : `${tag.color}20`,
                    color: isSelected ? 'white' : tag.color,
                  }}
                  title={t('tag.linkCount', {
                    name: tag.name,
                    count: String(tag.usageCount ?? 0),
                  })}
                >
                  {tag.name}
                  <span className="ml-1 text-xs opacity-70 3xl:ml-1.5 3xl:text-sm 7xl:text-base">
                    {tag.usageCount}
                  </span>
                </motion.button>

                {/* 태그 삭제 버튼 */}
                {onDeleteTag && (
                  <button
                    onClick={e => handleDeleteTag(tag._id, e)}
                    className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white opacity-0 transition-opacity duration-200 hover:bg-red-600 group-hover:opacity-100 3xl:h-5 3xl:w-5 3xl:text-sm 7xl:h-6 7xl:w-6 7xl:text-base"
                    title={t('tag.deleteTag', { name: tag.name })}
                  >
                    ×
                  </button>
                )}
              </motion.div>
            )
          })
        ) : (
          <div className="py-2 text-xs text-gray-500 dark:text-gray-400 3xl:py-2.5 3xl:text-sm 7xl:py-3 7xl:text-base">
            {searchQuery ? t('tag.noResults') : t('tag.empty')}
          </div>
        )}
      </div>

      {/* 태그 통계 */}
      {tags.length > 0 && (
        <div className="mt-3 border-t border-gray-100 pt-2 dark:border-gray-700 3xl:mt-4 3xl:pt-2.5 7xl:mt-5 7xl:pt-3">
          <div className="text-xs text-gray-500 dark:text-gray-400 3xl:text-sm 7xl:text-base">
            {t('tag.totalTags', { count: String(tags.length) })}
            {selectedTags.length > 0 &&
              t('tag.selectedCount', { count: String(selectedTags.length) })}
          </div>
        </div>
      )}
    </div>
  )
}

export default TagCloud
